import {
  DEFAULT_PRODUCTION_RATES, MODIFIER_MULTIPLIERS, DEFAULT_VISITS_PER_YEAR,
  PLANT_BEARING_TYPES,
} from './siteTakeoffConstants';

// Services where the effective SF should scale by plant density, not raw area.
// e.g. pruning a "Desert Planting — Low" area: only ~20% has plants to prune.
const DENSITY_SCALED_SERVICES = new Set([
  'pruning', 'trimming', 'deadheading',
]);

// What fraction of the area's SF is effectively worked, based on shrub density.
const DENSITY_COVERAGE = {
  none: 0.05,   // minimal — just a few scattered plants
  low:  0.20,   // < 30% cover
  med:  0.45,   // 30-60% cover
  high: 0.75,   // > 60% cover
};

/**
 * Get the compound modifier multiplier for an area based on its modifiers.
 */
function getModifierMultiplier(modifiers, multipliers) {
  if (!modifiers) return 1;
  let multiplier = 1;

  if (modifiers.access === 'obstructed') multiplier *= multipliers.access_obstructed;
  if (modifiers.slope === 'sloped') multiplier *= multipliers.slope_sloped;
  if (modifiers.litterExposure === 'high') multiplier *= multipliers.litterExposure_high;
  if (modifiers.weedPressure === 'med') multiplier *= multipliers.weedPressure_med;
  if (modifiers.weedPressure === 'high') multiplier *= multipliers.weedPressure_high;
  if (modifiers.limitedAccess) multiplier *= multipliers.limitedAccess;
  if (modifiers.blowRestrictions) multiplier *= multipliers.blowRestrictions;

  return multiplier;
}

/**
 * Get the input quantity and effective rate for a service.
 *
 * Special handling for `trees_or_sf_hr`:
 *   - If area has treeCount > 0: quantity = treeCount, rate = rateConfig.rate (trees/hr)
 *   - Else: quantity = density-scaled SF, rate = rateConfig.sfRate (SF/hr)
 *
 * Returns { quantity, effectiveRate } so the caller can compute hours = quantity / effectiveRate.
 */
function getQuantityAndRate(rateConfig, area, service) {
  // Hybrid tree-or-SF unit: trees first, fall back to density-scaled SF
  if (rateConfig.unit === 'trees_or_sf_hr') {
    const treeCount = area.plantDensity?.treeCount || 0;
    if (treeCount > 0) {
      return { quantity: treeCount, effectiveRate: rateConfig.rate, effectiveUnit: 'trees_hr' };
    }
    // Fall back to shrub SF with density scaling
    const baseSF = parseFloat(area.area) || 0;
    const sfRate = rateConfig.sfRate || 10000;
    if (PLANT_BEARING_TYPES.includes(area.areaType)) {
      const density = area.plantDensity?.shrubDensity || 'none';
      const coverage = DENSITY_COVERAGE[density] ?? 0.20;
      return { quantity: baseSF * coverage, effectiveRate: sfRate, effectiveUnit: 'sf_hr' };
    }
    return { quantity: baseSF, effectiveRate: sfRate, effectiveUnit: 'sf_hr' };
  }

  let quantity;
  switch (rateConfig.unit) {
    case 'lf_hr':
      quantity = area.geometryType === 'polyline'
        ? (parseFloat(area.length) || 0)
        : (parseFloat(area.perimeter) || 0);
      break;
    case 'trees_hr':
      quantity = area.plantDensity?.treeCount || 0;
      break;
    case 'sf_hr':
    default:
      quantity = parseFloat(area.area) || 0;
      // Apply density scaling for plant-touching services on plant-bearing areas
      if (DENSITY_SCALED_SERVICES.has(service) && PLANT_BEARING_TYPES.includes(area.areaType)) {
        const density = area.plantDensity?.shrubDensity || 'none';
        const coverage = DENSITY_COVERAGE[density] ?? 0.20;
        quantity = quantity * coverage;
      }
      break;
  }

  return { quantity, effectiveRate: rateConfig.rate, effectiveUnit: rateConfig.unit };
}

/**
 * Calculate labor hours for all areas.
 *
 * Returns two separate totals:
 * - routineHoursPerVisit: services performed every visit (visitsPerYear === totalVisitsPerYear)
 * - periodicServices: services performed less often, shown as "X hrs per application, Y times/year"
 *
 * @param {Array} areas
 * @param {Object} rateOverrides
 * @returns {Object}
 */
export function calculateLaborHours(areas, rateOverrides = {}) {
  if (!areas || areas.length === 0) {
    return {
      byArea: [],
      routineByService: {},
      periodicByService: {},
      routineHoursPerVisit: 0,
      periodicServices: [],
      grandTotalHours: 0,
      suggestedCrewSize: 1,
      suggestedVisitDuration: 0,
      totalVisitsPerYear: DEFAULT_VISITS_PER_YEAR,
    };
  }

  // Build net area map: for each parent area, compute gross minus exclusion SF
  const exclusionSFByParent = {};
  areas.forEach(a => {
    if (!a.parentAreaId) return;
    const pid = typeof a.parentAreaId === 'object' ? a.parentAreaId.toString() : a.parentAreaId;
    exclusionSFByParent[pid] = (exclusionSFByParent[pid] || 0) + (parseFloat(a.area) || 0);
  });

  // Filter out exclusion areas — they don't generate labor
  const workableAreas = areas.filter(a => !a.parentAreaId);

  const totalVisitsPerYear = rateOverrides._visitsPerYear || DEFAULT_VISITS_PER_YEAR;

  // Merge defaults with overrides
  const rates = {};
  Object.entries(DEFAULT_PRODUCTION_RATES).forEach(([svc, config]) => {
    const override = rateOverrides[svc] || {};
    rates[svc] = {
      ...config,
      rate: override.rate ?? config.rate,
      visitsPerYear: override.visitsPerYear ?? config.visitsPerYear,
      coveragePerVisit: override.coveragePerVisit ?? config.coveragePerVisit ?? 1.0,
    };
  });

  const modMultipliers = { ...MODIFIER_MULTIPLIERS, ...(rateOverrides._modifiers || {}) };

  const byArea = [];
  const routineByService = {};   // every-visit services
  const periodicByService = {};  // less-than-every-visit services

  workableAreas.forEach((area) => {
    const areaId = area._id || area.id;
    // Substitute net SF for parent areas that have exclusions
    const excSF = exclusionSFByParent[areaId] || 0;
    const effectiveArea = excSF > 0 ? { ...area, area: String((parseFloat(area.area) || 0) - excSF) } : area;
    const services = area.servicesRequired || [];
    if (services.length === 0) {
      byArea.push({
        areaId,
        areaName: area.name || '',
        areaType: area.areaType,
        services: [],
        routineHours: 0,
        totalHours: 0,
      });
      return;
    }

    const multiplier = getModifierMultiplier(area.modifiers, modMultipliers);
    const areaServices = [];
    let areaRoutineHours = 0;

    services.forEach((svc) => {
      const rateConfig = rates[svc];
      if (!rateConfig || rateConfig.rate <= 0) return;

      const { quantity, effectiveRate, effectiveUnit } = getQuantityAndRate(rateConfig, effectiveArea, svc);
      if (quantity <= 0 || effectiveRate <= 0) return;

      // Hours for one full application of this service on this area
      const hoursPerApplication = (quantity / effectiveRate) * multiplier;
      const coveragePerVisit = rateConfig.coveragePerVisit ?? 1.0;
      // hoursThisVisit = what actually happens each visit (reduced by coverage rotation)
      const hoursThisVisit = hoursPerApplication * coveragePerVisit;
      const isRoutine = rateConfig.visitsPerYear >= totalVisitsPerYear;

      const category = rateConfig.category || 'mtc';

      areaServices.push({
        service: svc,
        hoursPerApplication: hoursThisVisit,
        fullCoverageHours: hoursPerApplication,
        coveragePerVisit,
        quantity,
        rate: effectiveRate,
        unit: effectiveUnit,
        multiplier,
        visitsPerYear: rateConfig.visitsPerYear,
        isRoutine,
        category,
      });

      // Accumulate into the right bucket
      const bucket = isRoutine ? routineByService : periodicByService;
      if (!bucket[svc]) {
        bucket[svc] = {
          totalHoursPerApplication: 0,
          totalFullCoverageHours: 0,
          coveragePerVisit,
          areaCount: 0,
          visitsPerYear: rateConfig.visitsPerYear,
          category,
        };
      }
      bucket[svc].totalHoursPerApplication += hoursThisVisit;
      bucket[svc].totalFullCoverageHours += hoursPerApplication;
      bucket[svc].areaCount += 1;

      if (isRoutine) {
        areaRoutineHours += hoursThisVisit;
      }
    });

    byArea.push({
      areaId,
      areaName: area.name || '',
      areaType: area.areaType,
      services: areaServices,
      routineHours: areaRoutineHours,
      totalHours: areaServices.reduce((s, item) => s + item.hoursPerApplication, 0),
    });
  });

  // Routine = every-visit total
  const routineHoursPerVisit = Object.values(routineByService)
    .reduce((s, d) => s + d.totalHoursPerApplication, 0);

  // Periodic = sorted list of per-application totals
  const periodicServices = Object.entries(periodicByService)
    .map(([svc, data]) => ({
      service: svc,
      hoursPerApplication: data.totalHoursPerApplication,
      coveragePerVisit: data.coveragePerVisit,
      visitsPerYear: data.visitsPerYear,
      areaCount: data.areaCount,
      annualHours: data.totalHoursPerApplication * data.visitsPerYear,
      category: data.category,
    }))
    .sort((a, b) => b.annualHours - a.annualHours);

  // Build category summaries — all services (routine + periodic) bucketed by labor category
  const byCategory = {};  // { mtc: { routinePerVisit, periodic: [...], annualTotal } }
  const allServiceEntries = [
    ...Object.entries(routineByService).map(([svc, d]) => ({ svc, ...d, isRoutine: true })),
    ...Object.entries(periodicByService).map(([svc, d]) => ({ svc, ...d, isRoutine: false })),
  ];
  allServiceEntries.forEach(({ svc, totalHoursPerApplication, coveragePerVisit, visitsPerYear, areaCount, category, isRoutine }) => {
    const cat = category || 'mtc';
    if (!byCategory[cat]) {
      byCategory[cat] = { routinePerVisit: 0, periodicServices: [], annualTotal: 0 };
    }
    const annual = isRoutine
      ? totalHoursPerApplication * totalVisitsPerYear
      : totalHoursPerApplication * visitsPerYear;
    byCategory[cat].annualTotal += annual;

    if (isRoutine) {
      byCategory[cat].routinePerVisit += totalHoursPerApplication;
    } else {
      byCategory[cat].periodicServices.push({
        service: svc,
        hoursPerApplication: totalHoursPerApplication,
        coveragePerVisit,
        visitsPerYear,
        areaCount,
        annualHours: annual,
      });
    }
  });
  // Sort periodic within each category
  Object.values(byCategory).forEach(cat => {
    cat.periodicServices.sort((a, b) => b.annualHours - a.annualHours);
  });

  // Grand total
  const annualRoutine = routineHoursPerVisit * totalVisitsPerYear;
  const annualPeriodic = periodicServices.reduce((s, p) => s + p.annualHours, 0);
  const grandTotalAnnual = annualRoutine + annualPeriodic;

  // Crew size based on routine mtc only
  const mtcRoutine = byCategory.mtc?.routinePerVisit || routineHoursPerVisit;
  const suggestedCrewSize = Math.max(1, Math.ceil(mtcRoutine / 8));
  const suggestedVisitDuration = suggestedCrewSize > 0
    ? mtcRoutine / suggestedCrewSize
    : 0;

  return {
    byArea,
    routineByService,
    periodicByService,
    routineHoursPerVisit,
    periodicServices,
    byCategory,
    grandTotalAnnual,
    suggestedCrewSize,
    suggestedVisitDuration,
    totalVisitsPerYear,
  };
}
